import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { SlickCarouselComponent } from 'ngx-slick-carousel';
import { tap, distinctUntilChanged, filter } from 'rxjs/operators';
import { IAthleteBiography } from '../../../../common-page-components/stores/entities-store/athlete-biography-store';
import { ComboStoreComponent } from '../../../../common-page-components/stores/filters-store/filters-store';
import { BaseComponentComponent } from '../../../../library/components/components/common/base-component/base-component.component';

@Component({
  selector: 'app-athlete-medals',
  templateUrl: './athlete-medals.component.html',
  styleUrls: ['./athlete-medals.component.scss']
})
export class AthleteMedalsComponent extends BaseComponentComponent implements OnInit {
  /**
   * Combo filter for allow filter medals.
   *
   * @memberof AthleteMedalsComponent
   */
  comboFilter = new ComboStoreComponent('Category:', 'Name', 'medal-athlete-combo', undefined, undefined, undefined, undefined, [{
    item: {
      Name: "All"
    },
    position: 0,

  },
  {
    item: {
      Name: "Individual"
    },
    position: 1,
  },
  {
    item: {
      Name: "Mixed team"
    },
    position: 2,
  },
  {
    item: {
      Name: "Team"
    },
    position: 3,
  },

  ]);

  /**
   * The filters array.
   *
   * @memberof AthleteMedalsComponent
   */
  filterOptions = [this.comboFilter.filterInfo];

  /**
   * Input for get the medals from the athlete biography.
   *
   * @type {IAthleteBiography}
   * @memberof AthleteMedalsComponent
   */
  @Input() athlete: IAthleteBiography;

  /**
   * The array of property data by category. We use this because in mobile happens a problem when the data changes in slick after rendered.
   *
   * @memberof AthleteMedalsComponent
   */
  property_data = {
    'All': { data: [], config: undefined },
    'Individual': { data: [], config: undefined },
    'Mixed team': { data: [], config: undefined },
    'Team': { data: [], config: undefined },
  };

  /**
   * The property selected.
   *
   * @memberof AthleteMedalsComponent
   */
  property_selected = 'All';

  /**
   *-Variable for check if the athlete have medals and use in template on first load.
   *
   * @type {(boolean|undefined)}
   * @memberof AthleteMedalsComponent
   */
  haveMedals: boolean | undefined;

  constructor(private chg: ChangeDetectorRef) {
    super();
  }


  /**
   * Get the keys of property data.
   *
   * @returns
   * @memberof AthleteMedalsComponent
   */
  getKeys(){
    return Object.keys(this.property_data);
  }

  /**
   * {@inheritdoc}
   *
   * @memberof AthleteMedalsComponent
   */
  ngOnInit() {
    this.subscriptions.push(this.comboFilter.filterInfo.options.options_selected_subject.asObservable()
      .pipe(
        distinctUntilChanged(),
        tap(a => {
          // Set the property selected of the combo as selected.
          this.property_selected = a.Name;
        })).subscribe());
    // For each key on the property object compute the medals.
    this.getKeys().forEach(key=>{
      this.computeMedals(key);
    });

  }

  /**
   * Method for compute the category.
   *
   * @param {string} category The category name.
   * @memberof AthleteMedalsComponent
   */
  computeMedals(category: string) {
    // The filter function for filter the array bases on category.
    const filterFunc = ((value) => {
      switch (category) {
        case "All":
          return true;
        case "Individual":
          return value.IndividualTeam === 1;
        case "Mixed team":
          return value.IndividualTeam === 3;
        case "Team":
          return value.IndividualTeam === 4;
      }
    });
    const medalsWorldCupFinalInfo = Object.assign({ 'ComDescription': 'World Cup Final' },
      this.calculateMedals(this.athlete.Medals.filter(value => value.ComLevel === 3 && value.ComSubLevel === 13).filter(filterFunc)));

    const medalsWorldCupStagesInfo = Object.assign({ 'ComDescription': 'World Cup stages' },
      this.calculateMedals(this.athlete.Medals.filter(value => value.ComLevel === 3 && value.ComSubLevel === 12).filter(filterFunc)));

    const medalsWorldChampionShipInfo = Object.assign({ 'ComDescription': 'World Championships' },
      this.calculateMedals(this.athlete.Medals.filter(value => value.ComLevel === 2).filter(filterFunc)));
    const items =
      [
        Object.assign({}, medalsWorldCupStagesInfo),
        Object.assign({}, medalsWorldCupFinalInfo),
        Object.assign({}, medalsWorldChampionShipInfo)
      ]
        .filter(value => value.mustShow === true);
    const numberOfItems = items.length;
    // If the category is all, we need to setup there's medals available for let the view init.
    if (category === 'All') {
      this.haveMedals = items.length > 0;
    }
    // If we have medals que configure the carousel.
    if (items.length > 0) {
        const config = {
          slidesToShow: Math.min(Math.max(numberOfItems, 3), numberOfItems > 3 ? 3 : numberOfItems),
          slidesToScroll: Math.min(Math.max(numberOfItems, 3), numberOfItems > 3 ? 3 : numberOfItems),
          initialSlide: 0,
          dots: numberOfItems > 3,
          responsive: [
            {
              breakpoint: 900,
              settings: {
                dots: numberOfItems > 3,
                slidesToShow: Math.min(Math.max(numberOfItems, 3),
                  numberOfItems > 3 ? 3 : numberOfItems),
                slidesToScroll: Math.min(Math.max(numberOfItems, 3),
                  numberOfItems > 3 ? 3 : numberOfItems),
              }
            },
            {
              breakpoint: 750,
              settings: {
                dots: numberOfItems > 2,
                slidesToShow: Math.min(Math.max(numberOfItems, 2), numberOfItems > 2 ? 2 : numberOfItems),
                slidesToScroll: Math.min(Math.max(numberOfItems, 2), numberOfItems > 2 ? 2 : numberOfItems),
              }
            },
            {
              breakpoint: 500,
              settings: {
                dots: numberOfItems > 1,
                slidesToShow: Math.min(Math.max(numberOfItems, 1), numberOfItems > 1 ? 1 : numberOfItems),
                slidesToScroll: Math.min(Math.max(numberOfItems, 1), numberOfItems > 1 ? 1 : numberOfItems),
              }
            }
            ,
          ]
        };
        // Assign every value to the property category object.
        this.property_data[category].data = items;
        this.property_data[category].config = config;

    } else {
      // If no medals, se the slide config to undefined for display no results message.
      this.property_data[category].data = items;
      this.property_data[category].config = undefined;
    }

  }

  /**
   * Method for calculate the medals and group them based on gold, silver and bronze.
   *
   * @param {Array<any>} medals The medals array.
   * @returns The medals count as object.
   * @memberof AthleteMedalsComponent
   */
  calculateMedals(medals: Array<any>) {
    const bronze = medals.filter(value => value.Rnk === 3);
    const silver = medals.filter(value => value.Rnk === 2);
    const gold = medals.filter(value => value.Rnk === 1);
    return { gold: gold.length, silver: silver.length, bronze: bronze.length, mustShow: gold.length > 0 || silver.length > 0 || bronze.length > 0 }
  }

}
